import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import {
  Paper,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import {
  buildTimingModalidadesFromPlan,
  generateTimingModalidadesFromPlan,
  computeGruposConCategorias,
} from "../../../../../data-store/actions/events-actions";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: "20px",
    textAlign: "left",
    minHeight: "400px",
    overflowY: "scroll",
    backgroundColor: "#e0e0e0",
  },
  modalidadCard: {
    padding: 15,
    margin: "10px 0",
  },
  previewPaper: {
    padding: 15,
    marginTop: 10,
    backgroundColor: "#ffffff",
  },
}));

function buildDefaultDecisions(eventSubEventsRegistry) {
  const decisions = {};
  for (var key in eventSubEventsRegistry || {}) {
    decisions[key] = { action: "include" };
  }
  return decisions;
}

function contarCategorias(modalidad) {
  let total = 0;
  const generos = modalidad.ramaCat || {};
  for (var gen in generos) {
    total += Object.keys(generos[gen].categorias || {}).length;
  }
  return total;
}

export default function PlanTimingModalidades() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const eventDetail = useSelector((state) => state.events.eventDetail);
  const eventSubEventsRegistry = useSelector(
    (state) => state.events.eventSubEventsRegistry
  );

  const [decisions, setDecisions] = useState(() =>
    buildDefaultDecisions(eventSubEventsRegistry)
  );
  const [categoriasSeparadasPorGrupo, setCategoriasSeparadasPorGrupo] = useState(
    {}
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setDecisions(buildDefaultDecisions(eventSubEventsRegistry));
    setCategoriasSeparadasPorGrupo({});
  }, [eventSubEventsRegistry]);

  const gruposExistentes = useMemo(() => {
    const labels = Object.values(decisions)
      .filter((d) => d.action === "group" && d.groupLabel)
      .map((d) => d.groupLabel);
    return Array.from(new Set(labels));
  }, [decisions]);

  // Grupos resultantes de las decisiones actuales (uno por modalidad
  // "incluida" en solitario, o uno compartido por cada etiqueta de grupo),
  // ya con sus categorías combinadas por nombre -- esto es lo que permite
  // elegir qué separar DESPUÉS de agrupar, no antes.
  const grupos = useMemo(() => {
    if (!eventSubEventsRegistry) return [];
    try {
      return computeGruposConCategorias(decisions, eventSubEventsRegistry);
    } catch (error) {
      console.log("Error calculando los grupos del plan:", error);
      return [];
    }
  }, [decisions, eventSubEventsRegistry]);

  const plan = useMemo(
    () => ({ decisions, categoriasSeparadasPorGrupo }),
    [decisions, categoriasSeparadasPorGrupo]
  );

  const preview = useMemo(() => {
    if (!eventSubEventsRegistry) return {};
    try {
      return buildTimingModalidadesFromPlan(plan, eventSubEventsRegistry);
    } catch (error) {
      console.log("Error calculando la vista previa del plan:", error);
      return {};
    }
  }, [plan, eventSubEventsRegistry]);

  const hayGrupoSinEtiqueta = Object.values(decisions).some(
    (d) => d.action === "group" && !d.groupLabel
  );
  const todoExcluido =
    Object.keys(decisions).length > 0 &&
    Object.values(decisions).every((d) => d.action === "exclude");
  const puedeAplicar = !hayGrupoSinEtiqueta && !todoExcluido;

  function actualizarDecision(modKey, cambios) {
    setDecisions((prev) => ({
      ...prev,
      [modKey]: { ...prev[modKey], ...cambios },
    }));
  }

  function alternarCategoriaSeparada(claveGrupo, nombreNormalizado, marcar) {
    setCategoriasSeparadasPorGrupo((prev) => {
      const actuales = prev[claveGrupo] || [];
      const nuevas = marcar
        ? [...actuales, nombreNormalizado]
        : actuales.filter((n) => n !== nombreNormalizado);
      return { ...prev, [claveGrupo]: nuevas };
    });
  }

  function handleAplicarPlan() {
    const eventoIndex = "evento_" + eventDetail.iDEvento;
    dispatch(
      generateTimingModalidadesFromPlan(eventoIndex, plan, eventSubEventsRegistry)
    );
    setConfirmOpen(false);
  }

  if (!eventSubEventsRegistry) {
    return (
      <Paper className={classes.paper}>
        <Typography>Cargando modalidades de inscripción...</Typography>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Typography gutterBottom variant={"h4"} component="h2">
        Planificar modalidades de crono
      </Typography>
      <Typography gutterBottom variant={"body1"}>
        Primero decide, para cada modalidad de inscripción, si debe quedar tal
        cual, excluirse, o agruparse con otra(s). Después, más abajo, elige
        sobre el resultado ya agrupado qué categorías deben separarse en una
        modalidad aparte. Al aplicar, la configuración de crono se reemplaza
        por completo con exactamente lo que decidas aquí.
      </Typography>

      {Object.keys(eventSubEventsRegistry).map((modKey) => {
        const modalidad = eventSubEventsRegistry[modKey];
        const decision = decisions[modKey] || { action: "include" };

        return (
          <Paper key={modKey} className={classes.modalidadCard}>
            <Typography variant="h6">
              {modalidad.descripcionModalidad} ({modalidad.modalidad} km,{" "}
              {contarCategorias(modalidad)} categorías)
            </Typography>

            <RadioGroup
              row
              value={decision.action}
              onChange={(e) =>
                actualizarDecision(modKey, { action: e.target.value })
              }
            >
              <FormControlLabel
                value="include"
                control={<Radio />}
                label="Incluir tal cual"
              />
              <FormControlLabel
                value="exclude"
                control={<Radio />}
                label="Excluir"
              />
              <FormControlLabel
                value="group"
                control={<Radio />}
                label="Agrupar con..."
              />
            </RadioGroup>

            {decision.action === "group" && (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nombre del grupo"
                    fullWidth
                    value={decision.groupLabel || ""}
                    onChange={(e) =>
                      actualizarDecision(modKey, { groupLabel: e.target.value })
                    }
                  />
                </Grid>
                {gruposExistentes.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>O elige un grupo existente</InputLabel>
                      <Select
                        value={
                          gruposExistentes.indexOf(decision.groupLabel) >= 0
                            ? decision.groupLabel
                            : ""
                        }
                        onChange={(e) =>
                          actualizarDecision(modKey, {
                            groupLabel: e.target.value,
                          })
                        }
                      >
                        {gruposExistentes.map((label) => (
                          <MenuItem key={label} value={label}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>
        );
      })}

      <Divider style={{ margin: "20px 0" }} />

      <Typography gutterBottom variant={"h5"} component="h3">
        Separar categorías dentro de cada modalidad resultante
      </Typography>
      <Typography gutterBottom variant={"body2"}>
        Esto aplica sobre el resultado ya agrupado de arriba: si agrupaste
        varias modalidades en una, aquí ves sus categorías ya combinadas y
        puedes elegir cuáles deben salir a una modalidad de crono aparte (por
        ejemplo, porque corren una distancia distinta). Las que no marques
        quedan juntas en la modalidad resultante.
      </Typography>

      {grupos.map((grupo) => (
        <Paper key={grupo.clave} className={classes.modalidadCard}>
          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
            {grupo.nombreBase}
            {grupo.miembros.length > 1
              ? " (agrupa: " +
                grupo.miembros
                  .map((m) => eventSubEventsRegistry[m].descripcionModalidad)
                  .join(", ") +
                ")"
              : ""}
          </Typography>
          {grupo.categorias.map((cat) => {
            const seleccionadas =
              categoriasSeparadasPorGrupo[grupo.clave] || [];
            const marcada = seleccionadas.indexOf(cat.nombreNormalizado) >= 0;
            return (
              <FormControlLabel
                key={cat.nombreNormalizado}
                control={
                  <Checkbox
                    checked={marcada}
                    onChange={(e) =>
                      alternarCategoriaSeparada(
                        grupo.clave,
                        cat.nombreNormalizado,
                        e.target.checked
                      )
                    }
                  />
                }
                label={"Separar: " + cat.nombreCategoria}
              />
            );
          })}
        </Paper>
      ))}

      <Divider style={{ margin: "20px 0" }} />

      <Paper className={classes.previewPaper}>
        <Typography variant="h6" gutterBottom>
          Vista previa: así quedará el crono ({Object.keys(preview).length}{" "}
          modalidades)
        </Typography>
        {Object.values(preview).map((modalidad) => (
          <Typography key={modalidad.codigoModalidad} variant="body2">
            • {modalidad.descripcionModalidad} — {modalidad.modalidad} km —{" "}
            {contarCategorias(modalidad)} categorías
          </Typography>
        ))}
        {Object.keys(preview).length === 0 && (
          <Typography variant="body2" color="error">
            Con esta configuración no quedaría ninguna modalidad de crono.
          </Typography>
        )}
      </Paper>

      {hayGrupoSinEtiqueta && (
        <Typography color="error" style={{ marginTop: 10 }}>
          Hay una modalidad marcada como "Agrupar" sin nombre de grupo.
          Complétalo antes de aplicar.
        </Typography>
      )}

      <Button
        variant="contained"
        color="primary"
        style={{ marginTop: 20, padding: "15px 30px" }}
        disabled={!puedeAplicar}
        onClick={() => setConfirmOpen(true)}
      >
        Aplicar plan
      </Button>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Reemplazar configuración de crono</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esto va a reemplazar por completo la configuración actual de
            crono (modalidades y categorías) con el resultado de este plan.
            Si ya habías ajustado algo a mano en el crono (por ejemplo una
            distancia), se perderá. ¿Continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleAplicarPlan}>
            Sí, reemplazar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
